import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DeviceCategory } from "velavu-js-api";
import ConnectionStatus from "../data/connection-status";
import IconCircleDelete from "../dynamicicons/icon-circle-delete";
import IconReboot from "../dynamicicons/icon-reboot";
import {
	mapConnectionStatus,
	mapDeviceCategory,
} from "../helper/language-helper";
import ChipFilterDivider from "./chip-filter-divider";
import ChipFilterMenu from "./chip-filter-menu";
import * as styles from "./device-filter-menu.module.scss";
import DeviceIcon from "./device-icon";
import StatusIndicator from "./status-indicator";
import HoverTooltip from "./hover-tooltip";
import VelavuButton from "./velavu-button";
import VelavuSwitch from "./velavu-switch";
import { useDeviceModels } from "../components/main/device-model-provider";
import whitelabel from "../whitelabel";
import { getPermissionDeniedMessage } from "../helper/permission-helper";

export default function DeviceFilterMenu(props: {
	filterCategory: DeviceCategory | null;
	setFilterCategory: Dispatch<SetStateAction<DeviceCategory | null>>;
	filterModel: string | null;
	setFilterModel: Dispatch<SetStateAction<string | null>>;
	filterStatus: ConnectionStatus | null;
	setFilterStatus: Dispatch<SetStateAction<ConnectionStatus | null>>;
	filterUnpaired: boolean;
	setFilterUnpaired: Dispatch<SetStateAction<boolean>>;
	filterUnconfigured: boolean;
	setFilterUnconfigured: Dispatch<SetStateAction<boolean>>;
	selectedDevices: string[];
	isDirty: boolean;
	onApply: VoidFunction;
	onReboot: VoidFunction;
	onUnregister: VoidFunction;
	canReboot?: boolean;
	canUnregister?: boolean;
}) {
	const { t } = useTranslation();
	const propsFilterCategory = props.filterCategory;

	const categoryLabel = useCallback(
		(category: DeviceCategory | null) =>
			category === null
				? t("elements.filters.all")
				: mapDeviceCategory(category, t),
		[t],
	);

	const modelLabel = useCallback(
		(model: string | null) =>
			model === null ? t("elements.filters.all") : model,
		[t],
	);

	const statusLabel = useCallback(
		(status: ConnectionStatus | null) =>
			status === null
				? t("elements.filters.all")
				: mapConnectionStatus(status, t),
		[t],
	);

	//Fetch available device models
	const rawDeviceModels = useDeviceModels().deviceModels;

	//Filter out device models that are incompatible with the selected category
	const filteredDeviceModels = useMemo(() => {
		//Return an empty array if we're still waiting on device models
		if (rawDeviceModels === null) {
			return [];
		}

		//Don't filter anything if we haven't selected a category
		if (propsFilterCategory === null) {
			return rawDeviceModels;
		}

		//Filter out device models that don't match the category
		return rawDeviceModels.filter((model) => {
			return model.categories.includes(propsFilterCategory);
		});
	}, [rawDeviceModels, propsFilterCategory]);

	//Map the device models to their names (which is also their ID)
	const filteredDeviceModelNames = useMemo(() => {
		return filteredDeviceModels.map((model) => model.name);
	}, [filteredDeviceModels]);

	return (
		<div className={styles.container}>
			{props.selectedDevices.length > 0 ? (
				<>
					<div className={styles.selectedDevices}>
						<label>{t("elements.filters.selected")}</label>
						<div className={styles.selectedDevicesTally}>
							<label>{props.selectedDevices.length}</label>
						</div>
					</div>
					<ChipFilterDivider />
					<div className={styles.actionButtons}>
						<HoverTooltip
							tooltip={
								!props.canReboot
									? getPermissionDeniedMessage(
											"devices:manage",
											t,
										)
									: undefined
							}
						>
							<button
								className={styles.rebootButton}
								onClick={props.onReboot}
								disabled={!props.canReboot}
							>
								<IconReboot
									color={whitelabel.palette.secondary900}
									size={16}
								/>
								<span>{t("elements.filters.reboot")}</span>
							</button>
						</HoverTooltip>
						<HoverTooltip
							tooltip={
								!props.canUnregister
									? t(
											"common.permission_denied_contact_admin",
										)
									: undefined
							}
						>
							<button
								className={styles.unregisterButton}
								onClick={props.onUnregister}
								disabled={!props.canUnregister}
							>
								<IconCircleDelete
									color={whitelabel.palette.error900}
									size={16}
								/>
								<span>{t("elements.filters.unregister")}</span>
							</button>
						</HoverTooltip>
					</div>
				</>
			) : (
				<>
					<ChipFilterMenu
						title={t("elements.filters.type")}
						options={[null, ...Object.values(DeviceCategory)]}
						selected={props.filterCategory}
						setSelected={props.setFilterCategory}
						label={categoryLabel}
						keyExtractor={(category) =>
							category === null ? "all" : category
						}
					/>
					<ChipFilterDivider />
					<ChipFilterMenu
						title={t("elements.filters.device")}
						options={[null, ...filteredDeviceModelNames]}
						selected={props.filterModel}
						setSelected={props.setFilterModel}
						label={modelLabel}
						useDropdown
						dropdownMode="column"
						renderDropdownItem={(model) => (
							<div className={styles.deviceFilterItem}>
								<DeviceIcon model={model!} />
								<span style={{ marginLeft: 8 }}>{model}</span>
							</div>
						)}
						keyExtractor={(model) => model}
					/>
					<ChipFilterDivider />
					<ChipFilterMenu
						title={t("elements.filters.status")}
						options={[null, "active", "inactive", "registered"]}
						selected={props.filterStatus}
						setSelected={props.setFilterStatus}
						label={statusLabel}
						useDropdown
						dropdownMode="column"
						renderDropdownItem={(status) => (
							<div className={styles.deviceFilterItem}>
								<StatusIndicator status={status!} />
								<span style={{ marginLeft: 8 }}>
									{mapConnectionStatus(status!, t)}
								</span>
							</div>
						)}
					/>
					<ChipFilterDivider />
					<VelavuSwitch
						className={styles.devicePairedSwitch}
						label={t("elements.filters.unpaired")}
						toggled={props.filterUnpaired}
						onChange={() =>
							props.setFilterUnpaired(!props.filterUnpaired)
						}
					/>
					<VelavuSwitch
						className={styles.devicePairedSwitch}
						label={t("elements.filters.unconfigured")}
						toggled={props.filterUnconfigured}
						onChange={() =>
							props.setFilterUnconfigured(
								!props.filterUnconfigured,
							)
						}
					/>
					{props.isDirty && (
						<VelavuButton
							variant="outlined"
							label={t("common.apply")}
							onClick={props.onApply}
							className={styles.applyFiltersButton}
						/>
					)}
				</>
			)}
		</div>
	);
}
